import React, {useState} from "react";
import {
    Alert,
    Button,
    Dropdown,
    Modal,
    Space,
    Tag,
    Typography,
    message,
} from "antd";
import {
    CopyOutlined,
    DownOutlined,
    EyeOutlined,
    FlagOutlined,
    KeyOutlined,
    StopOutlined,
} from "@ant-design/icons";
import Api from "../api";
import {useViewAppAsUser} from "./user-impersonation";

const {Text} = Typography;

const UserAdminActions = ({user, userToken, onGetToken, onCopyToken, onArchive, onUserUpdated}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [flagLoading, setFlagLoading] = useState(false);
    const [tokenLoading, setTokenLoading] = useState(false);
    const viewAppAsUser = useViewAppAsUser(user?.user_id);

    const isFlagged = Number(user?.settings?.flagged_steps ?? 0) === 1;

    const handleGetToken = async () => {
        setTokenLoading(true);

        try {
            await onGetToken();
        } finally {
            setTokenLoading(false);
        }
    };

    const updateFlag = async (flaggedSteps) => {
        if (!user?.user_id) {
            return;
        }

        setFlagLoading(true);

        try {
            await Api.put(`/admin/v2/users/${user.user_id}/settings`, {
                settings: {flagged_steps: flaggedSteps},
            });
            message.success(flaggedSteps ? "User flagged for suspicious steps" : "Steps flag cleared");
            onUserUpdated?.();
            setMenuOpen(false);
        } finally {
            setFlagLoading(false);
        }
    };

    const confirmUpdateFlag = (flaggedSteps) => {
        Modal.confirm({
            title: flaggedSteps
                ? "Flag this user for suspicious steps?"
                : "Clear suspicious steps flag?",
            okText: flaggedSteps ? "Flag" : "Clear",
            okButtonProps: flaggedSteps ? {danger: true} : {},
            onOk: () => updateFlag(flaggedSteps),
        });
    };

    const confirmArchive = () => {
        Modal.confirm({
            title: "Sure to hide user and blog?",
            okText: "Hide",
            okButtonProps: {danger: true},
            onOk: async () => {
                await onArchive?.();
                setMenuOpen(false);
            },
        });
    };

    const menu = (
        <div className="user-admin-actions" onClick={(event) => event.stopPropagation()}>
            <Text type="secondary" className="user-admin-actions__label">Admin actions</Text>

            {viewAppAsUser.enabled && (
                <>
                    <Button
                        block
                        icon={<EyeOutlined/>}
                        loading={viewAppAsUser.starting}
                        disabled={viewAppAsUser.starting || !user?.user_id || !viewAppAsUser.settings || !!viewAppAsUser.handoff}
                        onClick={viewAppAsUser.start}
                    >
                        View app as user
                    </Button>

                    {!viewAppAsUser.settings && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Customer app origin and API base URL must be configured."
                        />
                    )}

                    {viewAppAsUser.error && (
                        <Alert type="error" showIcon message={viewAppAsUser.error}/>
                    )}

                    {viewAppAsUser.handoff && (
                        <Space direction="vertical" size={4}>
                            <Text type="secondary" style={{fontSize: 12}}>
                                The tab could not be opened. Open the user app within 60 seconds.
                            </Text>
                            <a
                                href={viewAppAsUser.handoff.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={viewAppAsUser.openHandoff}
                            >
                                Open user app
                            </a>
                        </Space>
                    )}

                    <div className="user-admin-actions__divider"/>
                </>
            )}

            <Button
                block
                icon={<KeyOutlined/>}
                loading={tokenLoading}
                onClick={handleGetToken}
            >
                Get access token
            </Button>

            <Button
                block
                icon={<CopyOutlined/>}
                disabled={!userToken}
                onClick={onCopyToken}
            >
                Copy access token
            </Button>

            <div className="user-admin-actions__divider"/>

            {isFlagged ? (
                <Button
                    block
                    icon={<FlagOutlined/>}
                    loading={flagLoading}
                    onClick={() => confirmUpdateFlag(0)}
                >
                    Clear steps flag
                </Button>
            ) : (
                <Button
                    block
                    icon={<FlagOutlined/>}
                    loading={flagLoading}
                    danger
                    onClick={() => confirmUpdateFlag(1)}
                >
                    Flag suspicious steps
                </Button>
            )}

            <div className="user-admin-actions__divider"/>

            <Button block icon={<StopOutlined/>} danger onClick={confirmArchive}>
                Hide user & blog
            </Button>
        </div>
    );

    return (
        <Space wrap className="user-page__actions">
            {isFlagged && (
                <Tag color="orange" icon={<FlagOutlined/>}>
                    Suspicious steps
                </Tag>
            )}
            <Dropdown
                open={menuOpen}
                onOpenChange={setMenuOpen}
                dropdownRender={() => menu}
                trigger={["click"]}
                placement="bottomRight"
            >
                <Button>
                    Actions <DownOutlined/>
                </Button>
            </Dropdown>
        </Space>
    );
};

export default UserAdminActions;
